import { useState, useRef, useEffect } from 'react';
import { Send, MessageCircle, X } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { CommentItem } from './CommentItem';
import { MentionInput, MentionInputRef } from './MentionInput';
import { TypingIndicator } from './TypingIndicator';
import { ReportDialog } from '@/components/reports/ReportDialog';
import { Comment } from '@/hooks/useComments';
import { useCommentReactions } from '@/hooks/useCommentReactions';
import { useReports } from '@/hooks/useReports';
import { useAuth } from '@/hooks/useAuth';
import { useTypingIndicator } from '@/hooks/useTypingIndicator';
import { toast } from 'sonner';

interface CommentsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pollId: string;
  onAuthRequired: () => void;
  // Lifted from parent to share state
  comments: Comment[];
  isLoading: boolean;
  isSubmitting: boolean;
  addComment: (content: string, parentId?: string) => Promise<boolean>;
  deleteComment: (commentId: string) => Promise<boolean>;
  commentCount: number;
  /** When true, replaces the comment input with a gated message */
  gated?: boolean;
}

interface ReplyingTo {
  commentId: string;
  username: string;
}

export function CommentsSheet({ 
  open, 
  onOpenChange, 
  pollId, 
  onAuthRequired,
  comments,
  isLoading,
  isSubmitting,
  addComment,
  deleteComment,
  commentCount,
  gated = false,
}: CommentsSheetProps) {
  const { user } = useAuth();
  const { getReactionsForComment, toggleReaction } = useCommentReactions(pollId);
  const { reportComment, isSubmitting: isReporting } = useReports();
  const { typingUsers, startTyping, stopTyping } = useTypingIndicator(`comments:${pollId}`);
  
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<ReplyingTo | null>(null);
  const [reportingCommentId, setReportingCommentId] = useState<string | null>(null);
  const mentionInputRef = useRef<MentionInputRef>(null);

  // Auto-focus input when sheet opens for immediate typing
  useEffect(() => {
    if (open && mentionInputRef.current && user) {
      // Small delay to ensure the sheet animation has started and input is mounted
      const timer = setTimeout(() => {
        mentionInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, user]);

  // Focus input when replying
  useEffect(() => {
    if (replyingTo && mentionInputRef.current) {
      mentionInputRef.current.focus();
    }
  }, [replyingTo]);

  const handleSubmit = async () => {
    if (!user) {
      onAuthRequired();
      return;
    }

    stopTyping();
    const success = await addComment(newComment, replyingTo?.commentId);
    if (success) {
      setNewComment('');
      setReplyingTo(null);
      // Auto-close sheet after successful comment submission
      onOpenChange(false);
    }
  };

  const handleInputChange = (value: string) => {
    setNewComment(value);
    if (value.trim()) {
      startTyping();
    } else {
      stopTyping();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleReply = (commentId: string, username: string) => {
    if (!user) {
      onAuthRequired();
      return;
    }
    setReplyingTo({ commentId, username });
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleReportComment = async (reason: 'spam' | 'harassment' | 'inappropriate' | 'other', description?: string) => {
    if (reportingCommentId) {
      const success = await reportComment(reportingCommentId, reason, description);
      if (success) {
        setReportingCommentId(null);
      }
    }
  };

  const headerContent = (
    <div className="flex items-center justify-between px-4 pb-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <MessageCircle className="h-5 w-5" />
        Comments {!isLoading && `(${commentCount})`}
      </h2>
      <Button 
        variant="ghost" 
        size="icon" 
        onClick={() => onOpenChange(false)}
        className="h-8 w-8"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <>
      <AnimatePresence>
        <DraggableBottomSheet
          open={open}
          onOpenChange={onOpenChange}
          height="70vh"
          customHeader={headerContent}
        >
          <div className="flex flex-col h-full">
            <ScrollArea className="flex-1 px-4">
              {isLoading ? (
                <div className="space-y-4 py-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : comments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <MessageCircle className="h-12 w-12 text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No comments yet</p>
                  <p className="text-sm text-muted-foreground/70">Be the first to share your thoughts!</p>
                </div>
              ) : (
                <div className="divide-y py-2">
                  {comments.map(comment => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                      reactions={getReactionsForComment(comment.id)}
                      onDelete={deleteComment}
                      onReport={(commentId) => setReportingCommentId(commentId)}
                      onReply={handleReply}
                      onReaction={(commentId, emoji) => {
                        if (!user) {
                          onAuthRequired();
                          return;
                        }
                        toggleReaction(commentId, emoji).catch(() => {
                          toast.error('Failed to add reaction');
                        });
                      }}
                      getReactionsForComment={getReactionsForComment}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
            
            <div className="p-4 border-t bg-background">
              {gated ? (
                <div className="flex items-center justify-center py-2 text-sm text-muted-foreground">
                  <span>Save 3 items to join the conversation</span>
                </div>
              ) : (
                <>
                  {/* Typing indicator */}
                  <TypingIndicator typingUsers={typingUsers} />
                  
                  {/* Reply indicator */}
                  {replyingTo && (
                    <div className="flex items-center justify-between mb-2 px-3 py-2 bg-muted rounded-lg">
                      <span className="text-sm text-muted-foreground">
                        Replying to <span className="font-medium text-foreground">{replyingTo.username}</span>
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={cancelReply}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <MentionInput
                      ref={mentionInputRef}
                      placeholder={user ? (replyingTo ? "Write a reply..." : "Add a comment...") : "Sign in to comment"}
                      value={newComment}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        setTimeout(() => {
                          mentionInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      onBlur={stopTyping}
                      disabled={!user || isSubmitting}
                      rows={1}
                    />
                    <Button
                      size="icon"
                      onClick={handleSubmit}
                      disabled={!newComment.trim() || isSubmitting}
                      className="flex-shrink-0 h-11 w-11"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </DraggableBottomSheet>
      </AnimatePresence>

      <ReportDialog
        open={!!reportingCommentId}
        onOpenChange={(open) => !open && setReportingCommentId(null)}
        onSubmit={handleReportComment}
        isSubmitting={isReporting}
        contentType="comment"
      />
    </>
  );
}
